/**
 * Login page — single screen with OAuth, magic link, and inline beta code.
 * Also doubles as beta activation screen for unverified users.
 */
import { useState, useEffect } from "react";
import { Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth-context.tsx";
import { checkupWizardPath, recordWaitlist, stashCheckupToken, stashPostAuthRedirect } from "../lib/api.ts";

const COUNTRIES_ROTATE = ["Kenya", "Nigeria", "South Africa", "Ghana", "Namibia", "Zambia"];

const COUNTRY_OPTIONS = [
  { value: "KE", label: "Kenya" },
  { value: "NG", label: "Nigeria" },
  { value: "ZA", label: "South Africa" },
  { value: "MULTI", label: "Multiple countries" },
];

const CAPACITY_OPTIONS = [
  { value: "100kW-1MW", label: "100 kW - 1 MW" },
  { value: "1MW-5MW", label: "1 MW - 5 MW" },
  { value: "5MW-20MW", label: "5 MW - 20 MW" },
  { value: "portfolio", label: "Portfolio (multiple projects)" },
];

// ─── Rotating tagline ───

function RotatingTagline() {
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => { setIndex((i) => (i + 1) % COUNTRIES_ROTATE.length); setFade(true); }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const longest = COUNTRIES_ROTATE.reduce((a, b) => a.length > b.length ? a : b);

  return (
    <p className="text-[13px] text-text-secondary text-center mt-4 italic">
      Illuminating your path to powering{" "}
      <span className="inline-block text-left" style={{ width: `${longest.length * 0.6}em` }}>
        <span className="font-semibold text-brand" style={{
          transition: "opacity 0.3s ease, transform 0.3s ease",
          opacity: fade ? 1 : 0,
          transform: fade ? "translateY(0)" : "translateY(4px)",
          display: "inline-block",
        }}>
          {COUNTRIES_ROTATE[index]}
        </span>
      </span>
    </p>
  );
}

// ─── Icons ───

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

// ─── Beta Activation Screen (shown after auth for unverified users) ───

export function BetaActivation() {
  const { verifyBeta, logOut } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);

  // Waitlist form state
  const [wlName, setWlName] = useState("");
  const [wlEmail, setWlEmail] = useState("");
  const [wlCompany, setWlCompany] = useState("");
  const [wlCountry, setWlCountry] = useState("KE");
  const [wlCapacity, setWlCapacity] = useState("1MW-5MW");
  const [wlDone, setWlDone] = useState(false);
  const [wlBusy, setWlBusy] = useState(false);
  const [wlError, setWlError] = useState<string | null>(null);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await verifyBeta(code);
    if (result.error) setError(result.error);
    setLoading(false);
  }

  async function handleWaitlist(e: React.FormEvent) {
    e.preventDefault();
    if (wlBusy) return; // double-submit = duplicate rows; the table has no unique constraint
    setWlError(null);
    setWlBusy(true);
    try {
      // Via the server (rate-gated, validated, service-role write) — the SPA no longer
      // writes the waitlist table directly. Duplicates count as success server-side.
      const recorded = await recordWaitlist({
        source: "login",
        name: wlName,
        email: wlEmail,
        company: wlCompany,
        country: wlCountry,
        capacity: wlCapacity,
      });
      if (recorded) setWlDone(true);
      else setWlError("Couldn't record that right now — please try again.");
    } catch (e: any) {
      setWlError(e.message);
    } finally {
      setWlBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Zap size={28} className="text-brand" />
          <span className="text-[25px] font-bold text-brand" style={{ letterSpacing: "-0.02em" }}>enereg</span>
          <span className="beta-badge">Beta</span>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-6">
          {!showWaitlist ? (
            <>
              <h1 className="text-[20px] font-semibold text-center mb-1">Activate your account</h1>
              <p className="text-[14px] text-text-secondary text-center mb-5">
                Enter your beta access code to get started
              </p>

              <form onSubmit={handleVerify} className="space-y-3">
                <input type="text" value={code} onChange={e => setCode(e.target.value)}
                  placeholder="Beta access code" required autoFocus
                  className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30" />

                {error && <div className="text-[14px] text-danger bg-danger-light px-3 py-2 rounded-xl">{error}</div>}

                <button type="submit" disabled={loading}
                  className="w-full py-2 bg-brand text-on-solid rounded-xl text-[16px] font-medium hover:bg-brand/90 transition-colors disabled:opacity-50">
                  {loading ? "..." : "Activate"}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button onClick={() => setShowWaitlist(true)} className="text-[14px] text-text-secondary hover:text-brand">
                  No code? Request access
                </button>
              </div>

              <div className="mt-3 text-center">
                <button onClick={logOut} className="text-[13px] text-text-tertiary hover:text-text-secondary">
                  Sign out
                </button>
              </div>
            </>
          ) : wlDone ? (
            <div className="text-center py-4">
              <div className="text-[28px] mb-2">&#10003;</div>
              <h2 className="text-[17px] font-medium mb-2">You're on the list!</h2>
              {/* No "we'll send your code to X": there is no mailer in this codebase, and codes
                  go out by hand as teams are onboarded. Same rule as the checkup gate — don't
                  promise a mechanism that doesn't exist (checkup-promises.test.ts polices the
                  checkup page; this line is policed by review). */}
              <p className="text-[14px] text-text-secondary mb-4">
                Your request is recorded under <strong>{wlEmail}</strong>. Beta access codes go
                out in batches as we onboard new teams.
              </p>
              <button onClick={logOut} className="text-brand text-[14px] font-medium">Done</button>
            </div>
          ) : (
            <form onSubmit={handleWaitlist} className="space-y-3">
              <h2 className="text-[17px] font-medium text-center mb-1">Request beta access</h2>
              <p className="text-[13px] text-text-secondary text-center mb-3">Tell us about your project</p>

              <input type="text" value={wlName} onChange={e => setWlName(e.target.value)} placeholder="Your name" required
                className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30" />
              <input type="email" value={wlEmail} onChange={e => setWlEmail(e.target.value)} placeholder="Email address" required
                className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30" />
              <input type="text" value={wlCompany} onChange={e => setWlCompany(e.target.value)} placeholder="Company name" required
                className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30" />
              <select value={wlCountry} onChange={e => setWlCountry(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30 bg-surface">
                {COUNTRY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <select value={wlCapacity} onChange={e => setWlCapacity(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30 bg-surface">
                {CAPACITY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>

              {wlError && <div className="text-[14px] text-danger bg-danger-light px-3 py-2 rounded-xl">{wlError}</div>}

              <button type="submit" disabled={wlBusy}
                className="w-full py-2 bg-brand text-on-solid rounded-xl text-[16px] font-medium hover:bg-brand/90 transition-colors disabled:opacity-50">
                {wlBusy ? "Sending…" : "Request access"}
              </button>
              <p className="text-[12px] text-text-tertiary text-center leading-relaxed">
                We use your details only to contact you about Enereg. See our{" "}
                <Link to="/privacy" className="underline hover:text-text-secondary">Privacy Policy</Link>.
              </p>
              <button type="button" onClick={() => setShowWaitlist(false)}
                className="w-full text-center text-[14px] text-text-secondary hover:text-text-primary">Back</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Login Page ───

export function LoginPage() {
  const { sendMagicLink, oAuthSignIn, authError } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Pre-fill from checkup if coming from /checkup CTA
  const params = new URLSearchParams(window.location.search);
  const fromCheckup = params.get("from") === "checkup";
  const checkupToken = params.get("checkup_token") || "";
  const [email, setEmail] = useState(params.get("email") || "");

  // Store post-auth redirect for OAuth (survives the redirect). TOKENLESS by design:
  // sessionStorage has no TTL, so a token parked here would outlive the localStorage
  // stash's expiry and hand a bearer capability to whoever signs in next in this tab.
  // The wizard's stash fallback carries the token the last step on every path.
  useEffect(() => {
    if (fromCheckup) {
      stashPostAuthRedirect(checkupWizardPath());
    }
    // The token is also the INVITE for a first-time user (a completed checkup admits you —
    // see auth.ts checkAdmission). It has to outlive the SSO round-trip, which drops query params,
    // or they'd land on the activation screen being asked for a code they don't have. Stored in
    // localStorage (with a TTL) so it survives a magic-link NEW TAB, not just the same-tab OAuth
    // redirect.
    if (checkupToken) stashCheckupToken(checkupToken);
  }, [fromCheckup, checkupToken]);

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await sendMagicLink(email) as any;
    if (result.error) {
      setError(result.error);
    } else if (result.directLogin) {
      // Demo account logged in directly — auth context will update
    } else {
      setMagicLinkSent(true);
    }
    setLoading(false);
  }

  async function handleOAuth(provider: "google" | "azure") {
    setError(null);
    setLoading(true);
    const result = await oAuthSignIn(provider);
    if (result?.error) {
      setError(result.error);
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Zap size={28} className="text-brand" />
          <span className="text-[25px] font-bold text-brand" style={{ letterSpacing: "-0.02em" }}>enereg</span>
          <span className="beta-badge">Beta</span>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-6">

          {magicLinkSent ? (
            /* ─── Magic link sent confirmation ─── */
            <div className="text-center py-4">
              <div className="text-[28px] mb-2">&#9993;</div>
              <h2 className="text-[17px] font-medium mb-2">Check your email</h2>
              <p className="text-[14px] text-text-secondary mb-1">We sent a login link to</p>
              <p className="text-[14px] font-medium mb-4">{email}</p>
              <p className="text-[13px] text-text-tertiary mb-4">Click the link in the email to sign in.</p>
              <button onClick={() => { setMagicLinkSent(false); setError(null); }}
                className="text-brand text-[14px] font-medium">Back to login</button>
            </div>
          ) : (
            /* ─── Main login screen ─── */
            <>
              <h1 className="text-[20px] font-semibold text-center mb-1" style={{ letterSpacing: "-0.02em" }}>
                Welcome to enereg
              </h1>
              <p className="text-[14px] text-text-secondary text-center mb-5">
                Sign in to manage your regulatory projects
              </p>

              {authError && (
                <div className="text-[14px] text-danger bg-danger-light px-3 py-2 rounded-xl mb-4">{authError}</div>
              )}

              {/* OAuth buttons */}
              <div className="space-y-2 mb-4">
                <button onClick={() => handleOAuth("google")} disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border border-border rounded-xl text-[15px] font-medium hover:bg-surface-secondary transition-colors disabled:opacity-50">
                  <GoogleIcon /> Continue with Google
                </button>
                <button onClick={() => handleOAuth("azure")} disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border border-border rounded-xl text-[15px] font-medium hover:bg-surface-secondary transition-colors disabled:opacity-50">
                  <MicrosoftIcon /> Continue with Microsoft
                </button>
              </div>

              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[12px] text-text-tertiary uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Magic link form */}
              <form onSubmit={handleMagicLink} className="space-y-3">
                <div>
                  <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1">Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" required
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30" />
                </div>

                {error && <div className="text-[14px] text-danger bg-danger-light px-3 py-2 rounded-xl">{error}</div>}

                <button type="submit" disabled={loading}
                  className="w-full py-2.5 bg-brand text-on-solid rounded-xl text-[16px] font-medium hover:bg-brand/90 transition-colors disabled:opacity-50">
                  {loading ? "..." : "Send login link"}
                </button>
              </form>

              {/* Beta note */}
              <p className="mt-4 pt-3 border-t border-border text-[12px] text-text-tertiary text-center">
                New users will need a beta access code after signing in
              </p>
            </>
          )}
        </div>

        <RotatingTagline />
        <p className="text-[12px] text-text-tertiary text-center mt-1">
          Renewable regulatory approval automation
        </p>
        <p className="text-[12px] text-text-tertiary text-center mt-3">
          <Link to="/privacy" className="hover:text-text-secondary">Privacy Policy</Link>
          <span className="mx-1.5">&middot;</span>
          <Link to="/terms" className="hover:text-text-secondary">Terms of Service</Link>
        </p>
      </div>
    </div>
  );
}
